import { ErrorMessage } from "components/shared/ErrorMessage";
import { SpinnerButton } from "components/shared/SpinnerButton";
import { redirectActions } from "domain/redirect/redirectActions";
import { toastActions } from "domain/toast/toastActions";
import { userActions } from "domain/user/userActions";
import { userMessages } from "domain/user/userMessages";
import { verifyEmail } from "domain/user/verifyEmail";
import { verifyEmailMutationResponse } from "generatedQueries/verifyEmailMutation.graphql";
import { setToken } from "infrastructure/token";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useRelayEnvironment } from "react-relay";
import { Form } from "reactstrap";
import Label from "reactstrap/lib/Label";
import { PayloadError } from "relay-runtime";
import { routes } from "routes/routes";
import { Message } from "shared/components/Message";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { combine, regex, required } from "shared/validations/validations";

type VerifyCodeFormValues = {
    verificationCode: string | null;
};

export const VerifyCode: FC = () => {
    const { formatMessage } = useIntl();
    const environment = useRelayEnvironment();
    const dispatch = useDispatch();

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const useFormReturn = useForm<VerifyCodeFormValues>({ mode: "onBlur" });
    const { control, handleSubmit, register } = useFormReturn;

    return <FormProvider {...useFormReturn}>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate={true} role="form">
            <div className="form-group row">
                <Label className="col-form-label col-sm-2" for="verificationCode">
                    <Message message={userMessages.verificationCodeLabel} />
                </Label>
                <div className="col-sm-4">
                    <TextBox
                        id="verificationCode"
                        name="verificationCode"
                        control={control}
                        register={register}
                        type="text"
                        autoComplete="one-time-code"
                        placeholder={formatMessage(userMessages.verificationCodeLabel)}
                        options={combine<VerifyCodeFormValues, "verificationCode">(required(), regex(/^\d{0,6}$/))}
                        aria-required={true}
                    />
                </div>
            </div>
            <SpinnerButton
                color="primary"
                message={userMessages.confirmEmailLabel}
                isLoading={submitting}
                type="submit"
            />
            {error && <ErrorMessage className="mt-3 mb-0" message={userMessages.incorrectVerificationCodeMessage} />}
        </Form>
    </FormProvider>;

    function onSubmit({ verificationCode }: VerifyCodeFormValues) {
        if (!verificationCode) {
            return;
        }

        setSubmitting(true);
        verifyEmail(environment, verificationCode, onVerifyCompleted, onVerifyError);
    }

    function onVerifyCompleted(response: verifyEmailMutationResponse, errors?: PayloadError[] | null) {
        if ((errors && errors.length > 0) || !response.verifyLicensingAccountEmail || !response.verifyLicensingAccountEmail.token) {
            onVerifyError();
        } else {
            setToken(response.verifyLicensingAccountEmail.token);
            dispatch(userActions.setToken(response.verifyLicensingAccountEmail.token));
            dispatch(toastActions.customSuccessToast(userMessages.emailSuccessfullyVerifiedMessage));
            dispatch(redirectActions.redirectTo(routes.account.index.create({})));
        }
    }

    function onVerifyError() {
        setError(true);
        setSubmitting(false);
        dispatch(toastActions.toastFailure());
    }
};
